/* global google */
// this component needs Google API

import {
  useRef,
  useState,
  useEffect,
  useMemo,
  forwardRef,
  type CSSProperties,
  type ImgHTMLAttributes,
  type HTMLAttributes,
  type IframeHTMLAttributes,
} from 'react';
import { cn } from '@virginexperiencedays/tailwind';

import { useMarkers } from './useMarker';
import { IconLocationPinOutline } from '../../icons/IconLocationPin';

import type { Location } from './locationType';

export const MapView = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ children, className }, ref) => {
    return (
      <div ref={ref} className={cn('h-[400px] w-full flex-1', className)}>
        {children}
      </div>
    );
  }
);
MapView.displayName = 'MapView';

type MapComponentProps = {
  width: string | number;
  height: string | number;
  locations: Location[];
  zoom?: number;
};

const DEFAULT_ZOOM = 12;
const MAX_INFO_WINDOW_WIDTH = 300;

export const MapLocation = ({
  width,
  height,
  locations,
  zoom = DEFAULT_ZOOM,
}: MapComponentProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();

  // Create infoWindow on map level to make sure there's only one instance
  const infoWindow = useMemo(
    () => new google.maps.InfoWindow({ maxWidth: MAX_INFO_WINDOW_WIDTH }),
    []
  );

  // Make lat and lng numbers
  const markers = useMemo(
    () =>
      locations.map(({ lat, lng }) => ({
        lat: typeof lat === 'string' ? parseFloat(lat) : lat,
        lng: typeof lng === 'string' ? parseFloat(lng) : lng,
      })),
    [locations]
  );

  // Create bounds for map
  const bounds = useMemo(() => new google.maps.LatLngBounds(), []);

  // Add markers to bounds
  useEffect(() => {
    markers.forEach(({ lat, lng }) => bounds.extend({ lat, lng }));
  }, [bounds, markers]);

  useEffect(() => {
    // If map exists, fit bounds in the view, so they're automatically centered
    if (map) {
      map.fitBounds(bounds); // auto-zoom
      map.panToBounds(bounds); // auto-center
    }
  }, [map, bounds]);

  // Create and append map
  useEffect(() => {
    if (ref.current && !map) {
      setMap(
        new window.google.maps.Map(ref.current, {
          maxZoom: zoom,
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: false,
          mapTypeControlOptions: {
            mapTypeIds: [],
          },
        })
      );
    }
  }, [ref, map]);

  // Call the custom useMarkers hook
  useMarkers({
    map,
    infoWindow,
    locations,
    markers,
  });

  return (
    <div>
      <div
        ref={ref}
        className="h-[var(--map-height)] w-[var(--map-width)] rounded-lg"
        style={
          {
            '--map-height': height && `${height}px`,
            '--map-width': width && `${width}px`,
          } as CSSProperties
        }
      />
    </div>
  );
};

export const StaticMapImage = ({ className, ...rest }: ImgHTMLAttributes<HTMLImageElement>) => {
  return (
    <img
      className={cn('w-full rounded-lg object-fill', className)}
      decoding="async"
      loading="lazy"
      alt="Google Maps static"
      {...rest}
    />
  );
};

type SingleLocationMapViewProps = IframeHTMLAttributes<HTMLIFrameElement> & {
  mapApiKey: string;
  location: Location;
  className?: string;
};
export const SingleLocationMapView = ({
  mapApiKey,
  location,
  className,
  ...rest
}: SingleLocationMapViewProps) => {
  return (
    <iframe
      className={cn('rounded-lg border-0', className)}
      loading="lazy"
      allowFullScreen
      referrerPolicy="no-referrer-when-downgrade"
      src={`https://www.google.com/maps/embed/v1/place?key=${mapApiKey}&q=${encodeURIComponent(
        location.lat
      )}, ${encodeURIComponent(location.lng)}&zoom=10`}
      title={`${location.lat},${location.lng}`}
      {...rest}
    />
  );
};

type LocationCountProps = {
  locationCount: number;
  locationName: string;
  /** Optional: if provided, shows the number of locations only for a specific region */
  regionName?: string;
} & HTMLAttributes<HTMLDivElement>;

export const LocationCount = ({
  className,
  locationCount,
  locationName,
  regionName,
  ...rest
}: LocationCountProps) => {
  return (
    <div
      className={cn(
        'font-inter flex items-center gap-0.5 text-base',
        !regionName && 'font-semibold',
        className
      )}
      {...rest}
    >
      <IconLocationPinOutline />

      {regionName ? (
        <p>
          <span className="font-semibold">{regionName}</span> ({locationCount} Locations)
        </p>
      ) : (
        <>
          {(locationCount < 2 && <span>{locationName}</span>) || (
            <span>{locationCount} Locations across the UK</span>
          )}
        </>
      )}
    </div>
  );
};
